import type { RepositoryName } from 'cadenza/repository/repository-name';
import type { DataSourceId, DataSourceType } from 'cadenza/repository/data-source-id';
import type { DbFlavor } from 'cadenza/repository/db-flavor';

/**
 * @see net/disy/cadenza/workbook/web/dto/DatasourceDto.java
 */
export interface DataSource {
  name: DataSourceId;
  printName: string;
  repositoryName: RepositoryName;
  logicalRepositoryName?: RepositoryName;
  capabilities: DataSourceCapability[];
  type: DataSourceType;
  dbFlavor?: DbFlavor;
}

/**
 * Capabilities of datasources, that vary from type to type, e.g. RDBMS vs ElasticSearch vs ArcGIS
 * This should reflect net.disy.lib.datasource.DataSourceCapability and be updated accordingly.
 *
 * @deprecated The enum object exists for client JS code. In TS use the type.
 */
export const DataSourceCapability = {
  BASIC_OLAP: 'BASIC_OLAP',
  ADVANCED_OLAP: 'ADVANCED_OLAP',
  GEOMETRY_EXTENT_AGGREGATION: 'GEOMETRY_EXTENT_AGGREGATION',
  DISTINCT_VALUES_QUERY: 'DISTINCT_VALUES_QUERY',
  MIN_MAX_VALUES_QUERY: 'MIN_MAX_VALUES_QUERY',
  IP_ADDRESS_CONDITION: 'IP_ADDRESS_CONDITION',
  CADENZA_DB: 'CADENZA_DB',
  DATA_BROWSER_COMPATIBLE: 'DATA_BROWSER_COMPATIBLE',
  LAZY_ABLE: 'LAZY_ABLE',
  WORKBOOK_COMPATIBLE: 'WORKBOOK_COMPATIBLE',
  QUERY_METRICS: 'QUERY_METRICS',
  QL_CONDITIONS: 'QL_CONDITIONS',
  ASSOCIATIONS: 'ASSOCIATIONS'
} as const;

export type DataSourceCapability = keyof typeof DataSourceCapability;

/**
 * Check if a datasource has all the given capabilities.
 *
 * @param dataSource - The data source to check
 * @param capabilities - The capabilities to check
 * @return `true` if the datasource has the given capabilities
 */
export function hasCapabilities (dataSource: DataSource, capabilities: DataSourceCapability[]): boolean {
  return capabilities.every(capability => dataSource.capabilities.includes(capability));
}

/**
 * Check if a datasource has the given capability.
 *
 * @param dataSource - The data source to check
 * @param capability - The capability to check
 * @return `true` if the datasource has the given capability
 */
export function hasCapability (dataSource: DataSource, capability: DataSourceCapability): boolean {
  return dataSource.capabilities.includes(capability);
}
